import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  CreditCard,
  Gauge,
  Info,
  Loader2,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
  UserRoundCheck,
  UserRoundPlus,
  UsersRound,
  XCircle,
} from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { EVENTS, type CHIEvent } from "@/lib/eventsData";
import { trpc } from "@/lib/trpc";

const STATUS_STYLE = {
  no_activity: { color: "#94A3B8", background: "rgba(148,163,184,.12)", border: "rgba(148,163,184,.25)", icon: <CalendarClock size={13} /> },
  healthy: { color: "#6EE7B7", background: "rgba(16,185,129,.11)", border: "rgba(16,185,129,.26)", icon: <CheckCircle2 size={13} /> },
  monitoring: { color: "#67E8F9", background: "rgba(0,212,255,.11)", border: "rgba(0,212,255,.25)", icon: <Activity size={13} /> },
  action_required: { color: "#FCA5A5", background: "rgba(239,68,68,.12)", border: "rgba(239,68,68,.27)", icon: <AlertTriangle size={13} /> },
  configuration_required: { color: "#FCD34D", background: "rgba(245,166,35,.12)", border: "rgba(245,166,35,.28)", icon: <CreditCard size={13} /> },
} as const;

type StatusKey = keyof typeof STATUS_STYLE;
type RangePreset = "24h" | "7d" | "30d" | "90d" | "quarter" | "year" | "custom";

const RANGE_OPTIONS: Array<{ value: RangePreset; label: string }> = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "quarter", label: "This quarter" },
  { value: "year", label: "This year" },
  { value: "custom", label: "Custom range" },
];

function toDateInput(value: Date) {
  return value.toISOString().slice(0, 10);
}

function dateFromInput(value: string) {
  return new Date(`${value}T12:00:00.000Z`);
}

function rangeForPreset(preset: Exclude<RangePreset, "custom">, now = new Date()) {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (preset === "24h") {
    return { startDate: toDateInput(new Date(today.getTime() - 86_400_000)), endDate: toDateInput(today) };
  }
  if (preset === "7d") {
    return { startDate: toDateInput(new Date(today.getTime() - 6 * 86_400_000)), endDate: toDateInput(today) };
  }
  if (preset === "30d") {
    return { startDate: toDateInput(new Date(today.getTime() - 29 * 86_400_000)), endDate: toDateInput(today) };
  }
  if (preset === "90d") {
    return { startDate: toDateInput(new Date(today.getTime() - 89 * 86_400_000)), endDate: toDateInput(today) };
  }
  if (preset === "quarter") {
    const quarterStartMonth = Math.floor(today.getUTCMonth() / 3) * 3;
    return {
      startDate: toDateInput(new Date(Date.UTC(today.getUTCFullYear(), quarterStartMonth, 1))),
      endDate: toDateInput(new Date(Date.UTC(today.getUTCFullYear(), quarterStartMonth + 3, 0))),
    };
  }
  return {
    startDate: toDateInput(new Date(Date.UTC(today.getUTCFullYear(), 0, 1))),
    endDate: toDateInput(new Date(Date.UTC(today.getUTCFullYear(), 11, 31))),
  };
}

function summaryValue(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

function percentValue(value: number | null) {
  return value === null ? "—" : `${value.toFixed(value % 1 === 0 ? 0 : 1)}%`;
}

function displayDate(value: string, options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" }) {
  return dateFromInput(value).toLocaleDateString("en-US", options);
}

function displayRange(startDate: string, endDate: string) {
  return `${displayDate(startDate)} – ${displayDate(endDate)}`;
}

function abbreviatedDate(value: string) {
  return dateFromInput(value).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function metricLabel(value: number, noun: string) {
  return `${summaryValue(value)} ${noun}`;
}

function HealthBadge({ status, label }: { status: StatusKey; label: string }) {
  const style = STATUS_STYLE[status];
  return (
    <span className="operations-dashboard-status" style={{ borderColor: style.border, background: style.background, color: style.color }}>
      {style.icon}
      {label}
    </span>
  );
}

function KpiCard({ label, value, detail, accent, icon }: { label: string; value: string | number; detail: string; accent: string; icon: ReactNode }) {
  return (
    <article className="operations-dashboard-kpi" style={{ "--ops-accent": accent } as CSSProperties}>
      <div className="operations-dashboard-kpi__top">
        <span>{label}</span>
        <span className="operations-dashboard-kpi__icon">{icon}</span>
      </div>
      <strong>{typeof value === "number" ? summaryValue(value) : value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function SectionHeading({ eyebrow, title, description, action }: { eyebrow: string; title: string; description: string; action?: ReactNode }) {
  return (
    <div className="operations-dashboard-section-heading">
      <div>
        <span className="operations-dashboard-eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}

function DonutMetric({ value, label, detail, color, track = "rgba(255,255,255,.06)" }: { value: number | null; label: string; detail: string; color: string; track?: string }) {
  const safeValue = value === null ? 0 : Math.max(0, Math.min(value, 100));
  return (
    <div className="operations-dashboard-donut-metric">
      <div
        aria-hidden="true"
        className="operations-dashboard-donut"
        style={{ background: `conic-gradient(${color} ${safeValue * 3.6}deg, ${track} 0deg)` }}
      >
        <span>{value === null ? "—" : `${Math.round(safeValue)}%`}</span>
      </div>
      <div>
        <h3>{label}</h3>
        <p>{detail}</p>
      </div>
    </div>
  );
}

function EmptyEventsState() {
  return (
    <section className="operations-dashboard-empty" aria-labelledby="operations-event-empty-title">
      <CalendarClock size={28} aria-hidden="true" />
      <h3 id="operations-event-empty-title">No scheduled events in this calendar window</h3>
      <p>Choose a wider date range to inspect event readiness, speaker coverage, registrations, and attendance signals.</p>
    </section>
  );
}

export default function OperationsHealthView() {
  /*
   * Last seven days by default.
   *
   * The quarter was the wrong opening view for a page that answers "is anything
   * wrong right now": a quarter's worth of rows buries today's problem among
   * ninety days of resolved ones, and every visit began by narrowing the range.
   * The longer windows are still one click away for the times the question
   * really is about the quarter.
   */
  const initialRange = useMemo(() => rangeForPreset("7d"), []);
  const [preset, setPreset] = useState<RangePreset>("7d");
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);

  const managedEventsQuery = trpc.events.managedEvents.useQuery(undefined, { retry: false, refetchOnWindowFocus: false });
  const allEvents = useMemo(() => {
    const managedEvents = (managedEventsQuery.data ?? []) as CHIEvent[];
    const managedSlugs = new Set(managedEvents.map(event => event.slug));
    return [
      ...EVENTS.filter(event => !managedSlugs.has(event.slug)),
      ...managedEvents,
    ].sort((first, second) => first.date.localeCompare(second.date));
  }, [managedEventsQuery.data]);

  const selectedEvents = useMemo(
    () => allEvents.filter(event => event.date >= startDate && event.date <= endDate),
    [allEvents, startDate, endDate],
  );
  const eventSlugs = useMemo(() => selectedEvents.map(event => event.slug), [selectedEvents]);
  const daysSelected = useMemo(() => {
    const start = Date.parse(`${startDate}T00:00:00.000Z`);
    const end = Date.parse(`${endDate}T00:00:00.000Z`);
    return Number.isFinite(start) && Number.isFinite(end) && end >= start ? Math.floor((end - start) / 86_400_000) + 1 : 0;
  }, [startDate, endDate]);
  const rangeIsValid = daysSelected > 0 && daysSelected <= 366;
  const dashboardInput = useMemo(
    () => ({ startDate, endDate, rollingHours: preset === "24h" ? 24 as const : undefined, eventSlugs }),
    [startDate, endDate, preset, eventSlugs],
  );

  const dashboardQuery = trpc.events.operationalHealthDashboard.useQuery(dashboardInput, {
    enabled: rangeIsValid,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const operationsQuery = trpc.events.operationsHealth.useQuery(
    { eventSlugs },
    { enabled: rangeIsValid, retry: false, refetchOnWindowFocus: false },
  );

  const dashboard = dashboardQuery.data;
  const operationalRows = useMemo(() => new Map((operationsQuery.data ?? []).map(row => [row.eventSlug, row])), [operationsQuery.data]);
  const eventRecordMap = useMemo(() => new Map((dashboard?.events.eventRecords ?? []).map(record => [record.eventSlug, record])), [dashboard?.events.eventRecords]);
  const eventMap = useMemo(() => new Map(allEvents.map(event => [event.slug, event])), [allEvents]);
  const allQueriesLoading = managedEventsQuery.isLoading || dashboardQuery.isLoading || operationsQuery.isLoading;
  const requestError = managedEventsQuery.error ?? dashboardQuery.error ?? operationsQuery.error;

  const handlePresetChange = (nextPreset: RangePreset) => {
    setPreset(nextPreset);
    if (nextPreset === "custom") return;
    const range = rangeForPreset(nextPreset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const refresh = async () => {
    await Promise.all([
      managedEventsQuery.refetch(),
      dashboardQuery.refetch(),
      operationsQuery.refetch(),
    ]);
  };

  if (!rangeIsValid) {
    return (
      <div className="operations-dashboard operations-dashboard--validation" style={{ flex: 1 }}>
        <section className="operations-dashboard-error" role="alert" aria-labelledby="operations-dashboard-range-error">
          <CircleAlert size={30} aria-hidden="true" />
          <h1 id="operations-dashboard-range-error">Choose a valid review window</h1>
          <p>Operational Health supports a chronological period of up to 366 days. Adjust the start and end dates to continue.</p>
          <button type="button" onClick={() => handlePresetChange("quarter")}>Reset to this quarter</button>
        </section>
      </div>
    );
  }

  if (allQueriesLoading) {
    return (
      <div style={{ flex: 1, display: "grid", minHeight: 460, placeItems: "center", padding: "2rem", color: "#94A3B8" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".65rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
          <Loader2 className="animate-spin" size={18} aria-hidden="true" />
          Preparing current operational signals…
        </div>
      </div>
    );
  }

  if (requestError || !dashboard) {
    return (
      <div className="operations-dashboard operations-dashboard--validation" style={{ flex: 1 }}>
        <section className="operations-dashboard-error" role="alert" aria-labelledby="operations-dashboard-error">
          <CircleAlert size={30} aria-hidden="true" />
          <h1 id="operations-dashboard-error">Operational Health is unavailable</h1>
          <p>{requestError?.message ?? "The dashboard did not return a usable aggregate."}</p>
          <button type="button" onClick={() => void refresh()}>
            <RefreshCw size={14} aria-hidden="true" /> Try again
          </button>
        </section>
      </div>
    );
  }

  const checkoutTotal = dashboard.membership.checkout.started
    + dashboard.membership.checkout.completed
    + dashboard.membership.checkout.abandoned
    + dashboard.membership.checkout.failed;
  const isRollingWindow = dashboard.range.windowKind === "rolling";
  const reviewWindowLabel = isRollingWindow ? `Last ${dashboard.range.rollingHours} hours` : displayRange(startDate, endDate);
  const coverageColor = dashboard.events.speakerCoverageRate === null || dashboard.events.speakerCoverageRate === 100 ? "#6EE7B7" : "#FCD34D";
  const checkoutColor = dashboard.membership.checkout.completionRate === null || dashboard.membership.checkout.completionRate >= 50 ? "#67E8F9" : "#FCD34D";
  const healthStatus: StatusKey = dashboard.events.eventsNeedingReview > 0
    ? "action_required"
    : dashboard.events.failureSignals > 0
      ? "monitoring"
      : "healthy";
  const healthLabel = dashboard.events.eventsNeedingReview > 0
    ? `${dashboard.events.eventsNeedingReview} event${dashboard.events.eventsNeedingReview === 1 ? "" : "s"} need review`
    : dashboard.events.failureSignals > 0
      ? "Monitor recorded signals"
      : "No event review flags";

  return (
    <div className="operations-dashboard" style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
      <header className="operations-dashboard-hero">
        <div className="operations-dashboard-hero__content">
          <div className="operations-dashboard-hero__eyebrow"><Activity size={14} aria-hidden="true" /> Administration workspace</div>
          <h1>Operational Health</h1>
          <p>Read the community’s membership, checkout, and event signals in one privacy-safe review surface. Every metric is an aggregate; this view never fabricates attendance, revenue, or payment outcomes.</p>
        </div>
        <div className="operations-dashboard-hero__stamp" aria-label={`Selected period: ${reviewWindowLabel}`}>
          <CalendarDays size={17} aria-hidden="true" />
          <div>
            <span>Review window</span>
            <strong>{reviewWindowLabel}</strong>
          </div>
        </div>
      </header>

      <section className="operations-dashboard-controls" aria-label="Operational Health date controls">
        <div className="operations-dashboard-control-group">
          <label htmlFor="operations-health-range">Reporting period</label>
          <select
            id="operations-health-range"
            value={preset}
            onChange={event => handlePresetChange(event.target.value as RangePreset)}
          >
            {RANGE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        {preset === "custom" && (
          <div className="operations-dashboard-custom-dates">
            <div className="operations-dashboard-control-group">
              <label htmlFor="operations-health-start">Start date</label>
              <input id="operations-health-start" type="date" value={startDate} onChange={event => setStartDate(event.target.value)} />
            </div>
            <div className="operations-dashboard-control-group">
              <label htmlFor="operations-health-end">End date</label>
              <input id="operations-health-end" type="date" value={endDate} onChange={event => setEndDate(event.target.value)} />
            </div>
          </div>
        )}
        <div className="operations-dashboard-control-spacer" />
        <button type="button" className="operations-dashboard-refresh" onClick={() => void refresh()} disabled={dashboardQuery.isFetching || operationsQuery.isFetching}>
          <RefreshCw size={15} className={dashboardQuery.isFetching || operationsQuery.isFetching ? "animate-spin" : undefined} aria-hidden="true" />
          <span>Refresh</span>
        </button>
      </section>

      <section className="operations-dashboard-health-strip" aria-label="Overall operational health">
        <div className="operations-dashboard-health-strip__lead">
          <span className="operations-dashboard-health-orb" style={{ background: STATUS_STYLE[healthStatus].color, boxShadow: `0 0 0 7px ${STATUS_STYLE[healthStatus].background}` }} />
          <div>
            <span>Operational review</span>
            <strong>{healthLabel}</strong>
          </div>
        </div>
        <div className="operations-dashboard-health-strip__metric"><Gauge size={16} aria-hidden="true" /><span>{summaryValue(dashboard.events.eventsNeedingReview)}</span> review flags</div>
        <div className="operations-dashboard-health-strip__metric"><AlertTriangle size={16} aria-hidden="true" /><span>{summaryValue(dashboard.events.failureSignals)}</span> failure signals</div>
        <div className="operations-dashboard-health-strip__metric"><ShieldCheck size={16} aria-hidden="true" /> aggregate-only data</div>
      </section>

      <section className="operations-dashboard-membership" aria-labelledby="operations-membership-title">
        <SectionHeading
          eyebrow="Membership performance"
          title="Community membership at a glance"
          description={isRollingWindow ? "Account inventory is current; chart activity is restricted to the exact rolling 24-hour window." : `Account inventory is current; activity in the chart is limited to the ${summaryValue(dashboard.range.dayCount)}-day review window.`}
          action={<span className="operations-dashboard-section-status"><TrendingUp size={14} aria-hidden="true" /> Server-aggregated</span>}
        />

        <div className="operations-dashboard-kpi-grid">
          <KpiCard label="Total accounts" value={dashboard.membership.totalAccounts} detail={`${summaryValue(dashboard.membership.accountsCreatedInRange)} created in this window`} accent="#A855F7" icon={<UsersRound size={17} />} />
          <KpiCard label="Active paid" value={dashboard.membership.activePaid} detail={`${summaryValue(dashboard.membership.activeAccounts)} active accounts`} accent="#F5A623" icon={<UserRoundCheck size={17} />} />
          <KpiCard label="Active non-paid" value={dashboard.membership.activeNonPaid} detail="Active accounts without paid access" accent="#67E8F9" icon={<UsersRound size={17} />} />
          <KpiCard label="Paid conversion" value={percentValue(dashboard.membership.paidConversionRate)} detail="Total accounts with active paid access" accent="#6EE7B7" icon={<UserRoundPlus size={17} />} />
        </div>

        <div className="operations-dashboard-membership-grid">
          <article className="operations-dashboard-panel operations-dashboard-panel--chart">
            <div className="operations-dashboard-panel-heading">
              <div>
                <span>Activity over time</span>
                <h3>Account and checkout signals</h3>
              </div>
              <div className="operations-dashboard-chart-legend" aria-label="Chart legend">
                <span><i style={{ background: "#A855F7" }} />Accounts created</span>
                <span><i style={{ background: "#67E8F9" }} />Completed checkouts</span>
              </div>
            </div>
            <div className="operations-dashboard-chart" aria-label="Membership activity chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dashboard.membership.trend} margin={{ top: 6, right: 2, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="operationsAccounts" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#A855F7" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#A855F7" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="operationsCheckout" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#67E8F9" stopOpacity={0.42} />
                      <stop offset="100%" stopColor="#67E8F9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="rgba(148,163,184,.12)" />
                  <XAxis dataKey="date" tickFormatter={abbreviatedDate} tick={{ fill: "#64748B", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={28} />
                  <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    labelFormatter={value => displayDate(String(value))}
                    contentStyle={{ background: "#11182b", border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, boxShadow: "0 18px 40px rgba(0,0,0,.28)", color: "#E2E8F0", fontFamily: "Inter, sans-serif", fontSize: 12 }}
                    itemStyle={{ color: "#E2E8F0" }}
                    labelStyle={{ color: "#94A3B8", marginBottom: 6 }}
                  />
                  <Area type="monotone" dataKey="accountsCreated" name="Accounts created" stroke="#A855F7" fill="url(#operationsAccounts)" strokeWidth={2} />
                  <Area type="monotone" dataKey="checkoutsCompleted" name="Completed checkouts" stroke="#67E8F9" fill="url(#operationsCheckout)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </article>

          <article className="operations-dashboard-panel operations-dashboard-panel--checkout">
            <div className="operations-dashboard-panel-heading">
              <div>
                <span>Checkout resolution</span>
                <h3>Recorded membership attempts</h3>
              </div>
              <DonutMetric value={dashboard.membership.checkout.completionRate} label="Completed" detail="of recorded attempts" color={checkoutColor} />
            </div>
            {checkoutTotal === 0 ? (
              <p className="operations-dashboard-empty-copy">No membership checkout attempts were recorded in this window. The dashboard does not infer conversion from page views.</p>
            ) : (
              <div className="operations-dashboard-funnel" aria-label="Membership checkout breakdown">
                {[
                  { label: "Started", value: dashboard.membership.checkout.started, color: "#A855F7" },
                  { label: "Completed", value: dashboard.membership.checkout.completed, color: "#67E8F9" },
                  { label: "Abandoned", value: dashboard.membership.checkout.abandoned, color: "#F5A623" },
                  { label: "Failed", value: dashboard.membership.checkout.failed, color: "#EF4444" },
                ].map(item => (
                  <div key={item.label} className="operations-dashboard-funnel__row">
                    <div><span style={{ background: item.color }} />{item.label}</div>
                    <strong>{summaryValue(item.value)}</strong>
                    <div className="operations-dashboard-funnel__track"><i style={{ width: `${Math.max((item.value / checkoutTotal) * 100, item.value > 0 ? 4 : 0)}%`, background: item.color }} /></div>
                  </div>
                ))}
              </div>
            )}
          </article>
        </div>
      </section>

      <section className="operations-dashboard-events" aria-labelledby="operations-events-title">
        <SectionHeading
          eyebrow="Event readiness"
          title={isRollingWindow ? "Events overlapping the last 24 hours" : "Calendar-window event health"}
          description={`${isRollingWindow ? "Calendar dates overlapping the rolling activity window contain" : "The selected period contains"} ${summaryValue(dashboard.events.selectedEventCount)} scheduled event${dashboard.events.selectedEventCount === 1 ? "" : "s"}. Registration and attendance records are current state for those events, not a fabricated forecast.`}
          action={<HealthBadge status={healthStatus} label={healthLabel} />}
        />

        {dashboard.events.selectedEventCount === 0 ? <EmptyEventsState /> : (
          <>
            <div className="operations-dashboard-event-summary">
              <KpiCard label="Events in range" value={dashboard.events.selectedEventCount} detail="Selected calendar window" accent="#A855F7" icon={<CalendarDays size={17} />} />
              <KpiCard label="Speaker coverage" value={percentValue(dashboard.events.speakerCoverageRate)} detail={`${summaryValue(dashboard.events.eventsWithAssignedSpeakers)} event${dashboard.events.eventsWithAssignedSpeakers === 1 ? "" : "s"} with an assigned speaker`} accent={coverageColor} icon={<UserRoundCheck size={17} />} />
              <KpiCard label="Registrations / event" value={dashboard.events.averageConfirmedRegistrations ?? "—"} detail={`${summaryValue(dashboard.events.confirmedRegistrations)} confirmed registrations`} accent="#67E8F9" icon={<UsersRound size={17} />} />
              <KpiCard label="Attendees / event" value={dashboard.events.averageAttendedPerEvent ?? "—"} detail={`${summaryValue(dashboard.events.attended)} marked attended · ${summaryValue(dashboard.events.noShows)} no-show`} accent="#E91E8C" icon={<CheckCircle2 size={17} />} />
            </div>

            <div className="operations-dashboard-event-panel-grid">
              <article className="operations-dashboard-panel operations-dashboard-panel--coverage">
                <span className="operations-dashboard-panel-kicker">Program readiness</span>
                <DonutMetric
                  value={dashboard.events.speakerCoverageRate}
                  label="Assigned speaker coverage"
                  detail={`${summaryValue(dashboard.events.eventsWithAssignedSpeakers)} of ${summaryValue(dashboard.events.selectedEventCount)} scheduled events`}
                  color={coverageColor}
                />
                <p className="operations-dashboard-panel-note">Coverage counts formal speaker assignments, not display copy on a public event card.</p>
              </article>
              <article className="operations-dashboard-panel operations-dashboard-panel--signal">
                <span className="operations-dashboard-panel-kicker">Actionable signals</span>
                <div className="operations-dashboard-signal-stack">
                  <div><span className="operations-dashboard-signal-icon operations-dashboard-signal-icon--warning"><AlertTriangle size={16} /></span><p><strong>{summaryValue(dashboard.events.eventsNeedingReview)}</strong> event{dashboard.events.eventsNeedingReview === 1 ? "" : "s"} need an operational review</p></div>
                  <div><span className="operations-dashboard-signal-icon operations-dashboard-signal-icon--danger"><XCircle size={16} /></span><p><strong>{summaryValue(dashboard.events.failureSignals)}</strong> recorded failure signal{dashboard.events.failureSignals === 1 ? "" : "s"} across selected events</p></div>
                </div>
                <p className="operations-dashboard-panel-note">Signals are aggregate operational states, not attendee-level error payloads.</p>
              </article>
              <article className="operations-dashboard-panel operations-dashboard-panel--event-trend">
                <div className="operations-dashboard-panel-heading">
                  <div>
                    <span>Registration activity</span>
                    <h3>Confirmed event registrations over time</h3>
                  </div>
                  <span className="operations-dashboard-section-status"><TrendingUp size={14} aria-hidden="true" /> Period-bounded</span>
                </div>
                <div className="operations-dashboard-event-trend-chart" aria-label="Confirmed event registrations trend">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dashboard.events.registrationTrend} margin={{ top: 6, right: 2, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="operationsEventRegistrations" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#E91E8C" stopOpacity={0.52} />
                          <stop offset="100%" stopColor="#E91E8C" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="rgba(148,163,184,.12)" />
                      <XAxis dataKey="date" tickFormatter={abbreviatedDate} tick={{ fill: "#64748B", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={28} />
                      <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 10 }} tickLine={false} axisLine={false} />
                      <Tooltip
                        labelFormatter={value => displayDate(String(value))}
                        contentStyle={{ background: "#11182b", border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, boxShadow: "0 18px 40px rgba(0,0,0,.28)", color: "#E2E8F0", fontFamily: "Inter, sans-serif", fontSize: 12 }}
                        itemStyle={{ color: "#E2E8F0" }}
                        labelStyle={{ color: "#94A3B8", marginBottom: 6 }}
                      />
                      <Area type="monotone" dataKey="confirmedRegistrations" name="Confirmed registrations" stroke="#E91E8C" fill="url(#operationsEventRegistrations)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="operations-dashboard-panel-note">Shows durable registrations recorded during the selected period for events in this calendar window.</p>
              </article>
            </div>

            <section className="operations-dashboard-event-list" aria-label="Event health records">
              <div className="operations-dashboard-event-list__heading">
                <div>
                  <span>Event records</span>
                  <h3>Readiness and durable activity</h3>
                </div>
                <p><Info size={14} aria-hidden="true" /> Expand a card for current aggregate registration, journey, and payment-operation context.</p>
              </div>
              {selectedEvents.map(event => {
                const operation = operationalRows.get(event.slug);
                const record = eventRecordMap.get(event.slug);
                if (!record) return null;
                const expanded = expandedSlug === event.slug;
                const status = STATUS_STYLE[record.healthStatus];
                const failureCount = operation
                  ? operation.registrations.rejected + operation.journeyStates.paymentFailed + operation.journeyStates.rejected + operation.ledgerSignals.paymentFailed + operation.ledgerSignals.registrationRejected
                  : 0;
                const paymentSignal = operation ? operation.journeyStates.paymentSucceeded + operation.ledgerSignals.paymentSucceeded : 0;

                return (
                  <article key={event.slug} className="operations-dashboard-event-card" style={{ "--event-color": event.color, "--event-status-border": status.border } as CSSProperties}>
                    <div className="operations-dashboard-event-card__main">
                      <div className="operations-dashboard-event-card__identity">
                        <span className="operations-dashboard-event-card__dot" aria-hidden="true" />
                        <div>
                          <span>{event.displayDate} · {event.type === "in-person" ? event.location : "Virtual"}</span>
                          <h3>{event.title}</h3>
                        </div>
                      </div>
                      <HealthBadge status={record.healthStatus} label={record.healthLabel} />
                    </div>
                    <div className="operations-dashboard-event-card__metrics">
                      <div><span>Confirmed</span><strong>{summaryValue(record.confirmedRegistrations)}</strong></div>
                      <div><span>Attendance marked</span><strong>{summaryValue(record.attendanceMarked)}</strong></div>
                      <div><span>Assigned speakers</span><strong>{summaryValue(record.assignedSpeakerCount)}</strong></div>
                      <div><span>Failure signals</span><strong style={{ color: failureCount > 0 ? "#FCA5A5" : "#CBD5E1" }}>{summaryValue(failureCount)}</strong></div>
                    </div>
                    <div className="operations-dashboard-event-card__footer">
                      <span className="operations-dashboard-pricing-label">
                        <CreditCard size={13} aria-hidden="true" />
                        {operation?.pricing.pricingMode === "free" ? "Free registration" : operation?.pricing.pricingMode === "paid_external" ? "Paid · external ticketing" : operation?.pricing.checkoutAvailability === "available" ? "Paid · Stripe checkout" : "Payment setup status unavailable"}
                      </span>
                      <button type="button" aria-expanded={expanded} aria-controls={`operations-event-detail-${event.slug}`} onClick={() => setExpandedSlug(current => current === event.slug ? null : event.slug)}>
                        {expanded ? "Hide detail" : "View detail"}<ChevronDown size={14} style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }} aria-hidden="true" />
                      </button>
                    </div>
                    {expanded && operation && (
                      <div id={`operations-event-detail-${event.slug}`} className="operations-dashboard-event-card__detail">
                        <p className="operations-dashboard-event-card__health-detail" style={{ color: status.color }}>{operation.health.detail}</p>
                        <div className="operations-dashboard-event-card__detail-grid">
                          <div><span>Current attendee records</span><p>{metricLabel(operation.registrations.registered, "confirmed")} · {metricLabel(operation.registrations.waitlisted, "waitlisted")} · {metricLabel(operation.registrations.pendingReview, "under review")} · {metricLabel(operation.registrations.cancelled, "cancelled")} · {metricLabel(operation.registrations.rejected, "rejected")}</p></div>
                          <div><span>Journey states</span><p>{metricLabel(operation.journeyStates.registrationIntent, "intents")} · {metricLabel(operation.journeyStates.externalCheckoutStarted, "external starts")} · {metricLabel(operation.journeyStates.paymentSucceeded, "success signals")} · {metricLabel(operation.journeyStates.paymentFailed, "failed")}</p></div>
                          <div><span>Payment operations</span><p>{operation.pricing.pricingMode === "paid_internal" && operation.pricing.checkoutAvailability !== "available" ? "Stripe amount or Price ID is required before checkout can open." : `${metricLabel(paymentSignal, "success signals")} · ${metricLabel(operation.ledgerSignals.paymentFailed, "ledger failures")}`}</p></div>
                        </div>
                        {operation.latestFailures.length > 0 ? (
                          <div className="operations-dashboard-failure-list">
                            <span>Latest aggregate failure signals</span>
                            {operation.latestFailures.map(failure => (
                              <div key={`${failure.kind}-${failure.occurredAt.toISOString()}`}>
                                <p><XCircle size={13} aria-hidden="true" />{failure.summary}</p>
                                <time dateTime={failure.occurredAt.toISOString()}>{failure.occurredAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</time>
                              </div>
                            ))}
                          </div>
                        ) : operation.health.status !== "configuration_required" ? (
                          <p className="operations-dashboard-no-failure"><CheckCircle2 size={14} aria-hidden="true" /> No durable failure signals have been recorded for this event.</p>
                        ) : (
                          <p className="operations-dashboard-configuration-note"><Info size={14} aria-hidden="true" /> This is a setup state, not a failed payment. Save a Stripe amount or Price ID before offering internal checkout.</p>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </section>
          </>
        )}
      </section>

      <section className="operations-dashboard-safeguard" role="note" aria-label="Operational Health interpretation safeguard">
        <ShieldCheck size={18} aria-hidden="true" />
        <div><strong>Interpretation safeguard</strong><p>Membership metrics use the CRM’s deduplicated account inventory. A Ticket Tailor handoff represents an opened external checkout, not a verified purchase; a Stripe registration is counted only after a signature-verified payment webhook confirms it.</p></div>
        <ArrowRight size={17} aria-hidden="true" />
      </section>
    </div>
  );
}
